import { Calculator, CheckCircle, XCircle } from "lucide-react";
import type { FC } from "react";

interface CalculatorLogRendererProps {
  toolName: string;
  toolInput: Record<string, unknown>;
  result: unknown;
  isError: boolean;
  durationMs: number;
}

interface CalculatorResult {
  result?: number;
  operation?: string;
  a?: number;
  b?: number;
  error?: string;
}

/**
 * Calculator renderer for Execution Log panel
 * Shows rich calculator UI instead of raw JSON
 */
export const CalculatorLogRenderer: FC<CalculatorLogRendererProps> = ({
  toolName,
  toolInput,
  result,
  isError,
  durationMs,
}) => {
  // Parse result
  const parsedResult: CalculatorResult =
    typeof result === "string"
      ? (() => { try { return JSON.parse(result); } catch { return { result: result }; } })()
      : (result as CalculatorResult) || {};

  // Build expression from input
  const input = toolInput as { operation?: string; a?: number; b?: number; expression?: string };
  const expression = input.expression
    || (input.operation && input.a !== undefined && input.b !== undefined
        ? `${input.a} ${getOperatorSymbol(input.operation)} ${input.b}`
        : JSON.stringify(toolInput));

  return (
    <div className="border-l-2 border-cyan-500 pl-3 py-1">
      {/* Header */}
      <div className="flex items-center gap-2 text-cyan-400 mb-2">
        <Calculator className="w-4 h-4" />
        <span className="font-semibold">Calculator</span>
        <span className="text-muted-foreground">|</span>
        <span className="text-muted-foreground">{durationMs}ms</span>
        {isError ? (
          <XCircle className="w-3 h-3 text-red-400 ml-auto" />
        ) : (
          <CheckCircle className="w-3 h-3 text-green-400 ml-auto" />
        )}
      </div>

      {/* Calculator Display */}
      <div className="ml-2 bg-card rounded-lg overflow-hidden">
        {/* Expression */}
        <div className="px-3 py-2 border-b border-border font-mono text-foreground">
          {expression}
        </div>

        {/* Result */}
        <div className={`px-3 py-3 ${isError ? 'bg-red-950/30' : 'bg-emerald-950/30'}`}>
          {isError ? (
            <div className="text-red-400 text-sm">
              Error: {parsedResult.error || 'Calculation failed'}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">=</span>
              <span className="text-2xl font-bold text-emerald-400">
                {parsedResult.result ?? '?'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function getOperatorSymbol(operation: string): string {
  switch (operation?.toLowerCase()) {
    case 'add': return '+';
    case 'subtract': return '-';
    case 'multiply': return '×';
    case 'divide': return '÷';
    default: return operation || '?';
  }
}
